<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { shiftChangeRequestsApi, type ShiftChangeRequest } from '@/api/shift-change-requests';
import { usePermissions } from '@/composables/usePermissions';
import { useAccessibleCareers } from '@/composables/useAccessibleCareers';
import { useTableFilter } from '@/composables/useTableFilter';
import { useGroupFilterStore } from '@/stores/group-filter';
import ResponsiveDialog from '@/components/responsive-dialog.vue';
import FilterMultiSelect from '@/components/filter-multi-select.vue';
import GroupFilterSelect from '@/components/group-filter-select.vue';
import ShiftChangeRequestsHistory from './shift-change-requests-history.vue';

// Pending shift-change requests raised from the caregiver app. Approving delegates the
// shift mutation and push notifications to the model observer — same path as the
// emailed token flow, without depending on a 48-hour link.
const model = defineModel<boolean>({ default: false });

const { hasPermission } = usePermissions();
const canAct = computed(() => hasPermission.value('shifts', 'update'));

const queryClient = useQueryClient();
const errorText = ref('');

// Pendientes (actionable cards) and Historial (resolved requests table + export). The
// wider table needs more room than the cards.
const CARDS_MAX_WIDTH = 720;
const TABLE_MAX_WIDTH = 1100;
const tab = ref<'pending' | 'history'>('pending');
const maxWidth = computed(() => (tab.value === 'history' ? TABLE_MAX_WIDTH : CARDS_MAX_WIDTH));

// Group and career narrow the query server-side (the group selection is the one shared
// with the calendars; careers are local to this dialog, as in the payments list). The
// key extends the badge's `['shiftChangeRequests', 'pending']` so invalidations reach
// both while the badge keeps counting every pending request.
const { groupIds } = storeToRefs(useGroupFilterStore());
const { accessibleCareers } = useAccessibleCareers();
const careerIn = ref<string[]>([]);
const careerOptions = computed(() => [
  ...accessibleCareers.value,
  { title: 'Sin especialidad', value: 'without_career' },
]);
const filters = computed(() => ({
  aasmState: 'pending',
  groupIdIn: groupIds.value,
  caregiverCareerIn: careerIn.value,
}));

const { data: requests, isPending: loading } = useQuery({
  queryKey: ['shiftChangeRequests', 'pending', filters],
  queryFn: () => shiftChangeRequestsApi.index(filters.value),
  enabled: model,
});

// Free text is matched in the browser over the names the card shows. The clear icon
// resets the field to null, hence the nullable ref.
const search = ref<string | null>('');
const matchesSearch = useTableFilter([
  'patientFullName', 'caregiverFullName', 'requestedCaregiverFullName', 'humanReason',
]);
const visibleRequests = computed(() => (
  (requests.value ?? []).filter(request => matchesSearch('', search.value ?? '', request))
));
const filtersActive = computed(() => (
  !!search.value?.trim() || careerIn.value.length > 0 || groupIds.value.length > 0
));
const emptyText = computed(() => (filtersActive.value ?
  'No hay solicitudes pendientes para los filtros seleccionados.' :
  'No hay solicitudes de cambio pendientes.'));

const busyId = ref<number | null>(null);

const { mutate: act } = useMutation({
  mutationFn: ({ id, actionType }: { id: number; actionType: 'approve' | 'reject' }) =>
    shiftChangeRequestsApi.update(id, actionType),
  onMutate: ({ id }) => {
    errorText.value = '';
    busyId.value = id;
  },
  onError: (error: { response?: { data?: { errors?: string[] } } }) => {
    const serverErrors = error?.response?.data?.errors;
    errorText.value = serverErrors?.length ?
      serverErrors.join(' · ') :
      'No se pudo procesar la solicitud. Puede haber quedado obsoleta — revisa el turno.';
  },
  onSettled: () => {
    busyId.value = null;
    // Refetch even on error: a stale request may have changed server-side.
    queryClient.invalidateQueries({ queryKey: ['shiftChangeRequests', 'pending'] });
    // An approval or rejection moves the request from one tab to the other.
    queryClient.invalidateQueries({ queryKey: ['shiftChangeRequests', 'history'] });
    // Approving mutates the shift itself (times or caregiver), so the calendars
    // rendered behind this dialog must refetch too.
    queryClient.invalidateQueries({ queryKey: ['shiftsAsCalendar'] });
    queryClient.invalidateQueries({ queryKey: ['shiftsCalendarEvents'] });
  },
});

function isCaregiverChange(request: ShiftChangeRequest): boolean {
  return request.requestType === 'caregiver_change';
}

function currentLabel(request: ShiftChangeRequest): string {
  return isCaregiverChange(request) ? 'Profesional actual' : 'Horario actual';
}

function proposedLabel(request: ShiftChangeRequest): string {
  return isCaregiverChange(request) ? 'Reemplazo propuesto' : 'Horario propuesto';
}

function currentValue(request: ShiftChangeRequest): string {
  return (isCaregiverChange(request) ? request.caregiverFullName : request.humanCurrentTimes) ?? '';
}

function proposedValue(request: ShiftChangeRequest): string {
  return (isCaregiverChange(request) ?
    request.requestedCaregiverFullName :
    request.humanRequestedTimes) ?? '';
}

// Acting is destructive-ish (the shift mutates, the professional is notified), so both
// actions pass through an explicit confirmation step.
const confirming = ref<{ request: ShiftChangeRequest; actionType: 'approve' | 'reject' } | null>(null);
const confirmOpen = computed({
  get: () => confirming.value !== null,
  set: (value: boolean) => {
    if (!value) confirming.value = null;
  },
});
const confirmingApprove = computed(() => confirming.value?.actionType === 'approve');

const confirmMessage = computed(() => {
  if (!confirming.value) return '';

  const { request, actionType } = confirming.value;
  const verb = actionType === 'approve' ? 'aprobar' : 'rechazar';
  const change = isCaregiverChange(request) ? 'cambio de profesional' : 'cambio de horario';

  return `¿Confirmas que quieres ${verb} el ${change} del turno del ` +
    `${request.humanShiftDate} de ${request.patientFullName}?`;
});
const confirmHint = computed(() => (confirmingApprove.value ?
  'El turno se actualizará según lo propuesto y se notificará al profesional.' :
  'Se notificará al profesional que su solicitud fue rechazada.'));

function confirmAction() {
  if (!confirming.value) return;

  act({ id: confirming.value.request.id, actionType: confirming.value.actionType });
  confirming.value = null;
}
</script>

<template>
  <responsive-dialog
    v-model="model"
    title="Solicitudes de cambio de turno"
    :max-width="maxWidth"
    scrollable
  >
    <v-tabs
      v-model="tab"
      color="primary"
      class="mb-3"
    >
      <v-tab
        value="pending"
        data-testid="tab-pending"
      >
        Pendientes
      </v-tab>
      <v-tab
        value="history"
        data-testid="tab-history"
      >
        Historial
      </v-tab>
    </v-tabs>

    <v-window v-model="tab">
      <v-window-item value="pending">
        <v-alert
          v-if="errorText"
          type="error"
          variant="tonal"
          density="compact"
          closable
          class="mb-3"
          @click:close="errorText = ''"
        >
          {{ errorText }}
        </v-alert>

        <v-row
          dense
          class="mb-2"
        >
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model="search"
              data-testid="change-request-search"
              label="Buscar"
              prepend-inner-icon="mdi-magnify"
              single-line
              variant="underlined"
              hide-details
              clearable
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <filter-multi-select
              v-model="careerIn"
              :items="careerOptions"
              label="Especialidades"
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
            md="4"
          >
            <group-filter-select
              v-model="groupIds"
              hide-details
            />
          </v-col>
        </v-row>

        <div
          v-if="loading"
          class="d-flex justify-center py-8"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          />
        </div>
        <p
          v-else-if="visibleRequests.length === 0"
          class="text-center text-medium-emphasis py-8"
        >
          {{ emptyText }}
        </p>
        <template v-else>
          <v-card
            v-for="request in visibleRequests"
            :key="request.id"
            variant="outlined"
            class="request-card mb-4"
          >
            <div class="d-flex align-center ga-2 flex-wrap px-4 pt-3">
              <v-icon
                :icon="isCaregiverChange(request) ? 'mdi-account-switch' : 'mdi-clock-outline'"
                color="primary"
                size="20"
              />
              <span class="font-weight-bold">{{ request.humanRequestType }}</span>
              <span class="text-medium-emphasis">· turno del {{ request.humanShiftDate }}</span>
              <v-spacer />
              <span class="text-caption text-medium-emphasis">
                Solicitado el {{ request.humanCreatedAt }}
              </span>
            </div>

            <div class="d-flex ga-4 align-end flex-wrap px-4 pb-3">
              <div class="flex-grow-1">
                <div class="d-flex ga-8 flex-wrap pt-3">
                  <div>
                    <div class="text-caption text-medium-emphasis">
                      Paciente
                    </div>
                    <div class="font-weight-medium">
                      {{ request.patientFullName }}
                    </div>
                  </div>
                  <div>
                    <div class="text-caption text-medium-emphasis">
                      Solicitado por
                    </div>
                    <div>{{ request.caregiverFullName }}</div>
                    <div
                      v-if="request.caregiverHumanCareer"
                      class="text-caption text-medium-emphasis"
                    >
                      {{ request.caregiverHumanCareer }}
                    </div>
                  </div>
                </div>

                <div class="d-flex align-center ga-3 flex-wrap py-3">
                  <div class="change-box change-box--current">
                    <div class="text-caption text-medium-emphasis">
                      {{ currentLabel(request) }}
                    </div>
                    <div class="text-body-1">
                      {{ currentValue(request) }}
                    </div>
                  </div>
                  <v-icon
                    icon="mdi-arrow-right"
                    color="primary"
                  />
                  <div class="change-box change-box--proposed">
                    <div class="text-caption text-primary">
                      {{ proposedLabel(request) }}
                    </div>
                    <div class="text-body-1 font-weight-medium">
                      {{ proposedValue(request) }}
                    </div>
                  </div>
                </div>

                <div
                  v-if="request.humanReason || request.reason"
                  class="text-body-2 text-medium-emphasis"
                >
                  <div v-if="request.humanReason">
                    Motivo: <span class="font-weight-medium text-high-emphasis">{{ request.humanReason }}</span>
                  </div>
                  <div v-if="request.reason">
                    {{ request.humanReason ? 'Comentario' : 'Motivo' }}:
                    <span class="font-italic">“{{ request.reason }}”</span>
                  </div>
                </div>
              </div>

              <div
                v-if="canAct"
                class="d-flex flex-column ga-2 actions-stack"
              >
                <v-btn
                  size="small"
                  color="primary"
                  variant="flat"
                  :loading="busyId === request.id"
                  @click="confirming = { request, actionType: 'approve' }"
                >
                  Aprobar
                </v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  color="error"
                  :loading="busyId === request.id"
                  @click="confirming = { request, actionType: 'reject' }"
                >
                  Rechazar
                </v-btn>
              </div>
            </div>
          </v-card>
        </template>
      </v-window-item>
      <v-window-item value="history">
        <shift-change-requests-history v-if="tab === 'history'" />
      </v-window-item>
    </v-window>
  </responsive-dialog>

  <responsive-dialog
    v-model="confirmOpen"
    max-width="460"
    :mobile-fullscreen="false"
  >
    <v-card class="pa-2">
      <v-card-title class="d-flex align-center ga-2">
        <v-icon
          :color="confirmingApprove ? 'primary' : 'error'"
          :icon="confirmingApprove ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline'"
        />
        <span>{{ confirmingApprove ? 'Aprobar solicitud' : 'Rechazar solicitud' }}</span>
      </v-card-title>

      <v-card-text>
        <p class="text-body-2 mb-3">
          {{ confirmMessage }}
        </p>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{ confirmHint }}
        </p>
      </v-card-text>

      <v-card-actions class="px-4 pb-3 pt-0">
        <v-spacer />
        <v-btn
          variant="text"
          @click="confirmOpen = false"
        >
          Cancelar
        </v-btn>
        <v-btn
          :color="confirmingApprove ? 'primary' : 'error'"
          variant="flat"
          @click="confirmAction"
        >
          {{ confirmingApprove ? 'Sí, aprobar' : 'Sí, rechazar' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </responsive-dialog>
</template>

<style scoped>
.request-card {
  border-color: rgba(var(--v-border-color), var(--v-border-opacity));
}

.actions-stack {
  min-width: 116px;
}

.change-box {
  min-width: 160px;
  padding: 8px 16px;
  border-radius: 8px;
}

.change-box--current {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.change-box--proposed {
  border: 1px solid rgba(var(--v-theme-primary), 0.4);
  background: rgba(var(--v-theme-primary), 0.06);
}
</style>
